import {skipToken, useQuery} from '@tanstack/react-query';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {FollowedBrands, TopBrands} from './graphqlQuerires';
import {DashboardQueryV3Input} from '../../graphql-react-query/graphql';
import {Brand, Creative} from 'bigdatr-style';

type mediaValueMonthSpend = {
    month: string;
    spend: number;
};

export type DashboardBrands = {
    brands: {
        mediaValue: {
            previousPeriodTotal: number;
            currentPeriodTotal: number;
            change: number;
            hasAccess: boolean;
            currentPeriod: mediaValueMonthSpend[];
            previousPeriod: mediaValueMonthSpend[];
        };
        creatives: Creative[];
        brand: Brand;
    }[];
    paginationInfo: {
        currentPage: number;
        hasPreviousPage: boolean;
        hasNextPage: boolean;
    };
};

export function useFollowedBrandRequest(filter: DashboardQueryV3Input, segmentUpdatedAt: string) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: [
            'followedBrands',
            filter,
            filter.pagination,
            filter.dateRangeFilter,
            filter.creativeCount,
            segmentUpdatedAt
        ],
        queryFn: async () => {
            const response = await request(FollowedBrands, {input: filter});

            if (!response?.dashboardV3?.followedBrands?.brands) {
                throw new Error('Followed brands not returned from API');
            }

            const brands = response.dashboardV3.followedBrands.brands.map((data) => ({
                mediaValue: data.mediaValue,
                creatives: (data.creatives || []).map((creative) =>
                    creative ? new Creative(creative) : null
                ),
                brand: new Brand({
                    name: data.brand?.name ?? '',
                    id: data.brand?.id
                })
            }));

            return {
                brands,
                paginationInfo: {
                    currentPage:
                        response.dashboardV3.followedBrands.paginationInfo?.currentPage ?? 1,
                    hasPreviousPage:
                        response.dashboardV3.followedBrands.paginationInfo?.hasPreviousPage ??
                        false,
                    hasNextPage:
                        response.dashboardV3.followedBrands.paginationInfo?.hasNextPage ?? false
                }
            };
        }
    });
}

export function useTopBrandsRequest(
    filter: DashboardQueryV3Input,
    intersectionObserver?: IntersectionObserverEntry
) {
    const request = useClientMainAuthorizedFetch();
    const enabled = intersectionObserver?.isIntersecting;

    return useQuery({
        queryKey: [
            'topBrands',
            filter,
            filter.dateRangeFilter.relative?.months,
            intersectionObserver
        ],
        queryFn: enabled
            ? async () => {
                  const response = await request(TopBrands, {input: filter});

                  if (!response?.dashboardV3?.topBrands?.brands) {
                      throw new Error('Top brands not returned from API');
                  }

                  const brands = response.dashboardV3.topBrands.brands.map((data) => ({
                      mediaValue: data.mediaValue,
                      creatives: (data.creatives || []).map((creative) =>
                          creative ? new Creative(creative) : null
                      ),
                      brand: new Brand({
                          name: data.brand?.name ?? '',
                          id: data.brand?.id
                      })
                  }));

                  return {
                      brands,
                      paginationInfo: {
                          currentPage:
                              response.dashboardV3.topBrands.paginationInfo?.currentPage ?? 1,
                          hasPreviousPage:
                              response.dashboardV3.topBrands.paginationInfo?.hasPreviousPage ??
                              false,
                          hasNextPage:
                              response.dashboardV3.topBrands.paginationInfo?.hasNextPage ?? false
                      }
                  };
              }
            : skipToken
    });
}
